import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface ProjectSection {
  id?: number;
  sectionName: string;
  [key: string]: any;
}

interface ProjectSectionsContextType {
  sections: ProjectSection[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchSections: (page?: number, perPage?: number) => Promise<void>;
  addSection: (section: any) => Promise<void>;
  editSection: (section: any) => Promise<void>;
  deleteSection: (id: number) => Promise<void>;
}

export const ProjectSectionsContext = createContext<ProjectSectionsContextType | undefined>(undefined);

interface ProjectSectionsProviderProps {
  children: ReactNode;
}

const ProjectSectionsProvider: React.FC<ProjectSectionsProviderProps> = ({ children }) => {
  const [sections, setSections] = useState<ProjectSection[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all sections with pagination
  const fetchSections = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/sections?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let sectionsData: ProjectSection[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          sectionsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          sectionsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          sectionsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        sectionsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        sectionsData = responseData;
      }
      
      setSections(Array.isArray(sectionsData) ? sectionsData : []);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch sections:", error);
      setSections([]); // Set empty array on error
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new section
  const addSection = async (section: any): Promise<void> => {
    try {
      const response = await api.post("/sections", section);
      const newSection = response.data?.data || response.data;
      if (newSection) {
        setSections((prev) => [...prev, newSection]);
      }
    } catch (error) {
      console.error("Failed to add section:", error);
      throw error; // Re-throw to allow component to handle error
    }
  };

  // Edit an existing section
  const editSection = async (section: any): Promise<void> => {
    try {
      const response = await api.put(`/sections/${section.id}`, section);
      const updatedSection = response.data?.data || response.data;
      if (updatedSection) {
        setSections((prev) =>
          prev.map((item) => (item.id === section.id ? updatedSection : item))
        );
      }
    } catch (error) {
      console.error("Failed to edit section:", error);
      throw error; // Re-throw to allow component to handle error
    }
  };

  // Delete a section
  const deleteSection = async (id: number): Promise<void> => {
    try {
      await api.delete(`/sections/${id}`);
      setSections((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete section:", error);
      throw error; // Re-throw to allow component to handle error
    }
  };

  useEffect(() => {
    fetchSections(1, 15);
  }, [fetchSections]);

  return (
    <ProjectSectionsContext.Provider
      value={{
        sections,
        pagination,
        loading,
        fetchSections,
        addSection,
        editSection,
        deleteSection,
      }}
    >
      {children}
    </ProjectSectionsContext.Provider>
  );
};

export default ProjectSectionsProvider;
